import { Box } from '@zoopla-eng/zds-components/box';
import { Button, ButtonLeft } from '@zoopla-eng/zds-components/button';
import { ControlledDialog } from '@zoopla-eng/zds-components/dialog';
import { Divider } from '@zoopla-eng/zds-components/divider';
import { FiltersMedium } from '@zoopla-eng/zds-components/icons';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { Added, AddedToSite } from './AddedToSite';
import { Bedrooms, BedsMax, BedsMin } from './Bedrooms';
import { Features } from './Features';
import { labelStyle } from './Filters.styles.css';
import {
    HomeType,
    IsAuction,
    IsRetirementHome,
    IsSharedOwnership,
    NewHomes
} from './HomeType';
import { Keywords } from './Keywords';
import { Price, PriceFrequency, PriceMax, PriceMin } from './Price';
import {
    BuyerIncentive,
    ChainFree,
    IncludeSold,
    PropertyStatus,
    ReducedPrice
} from './PropertyStatus';
import { PropertySubType, PropertyType } from './PropertyType';
import { getDuration, getTransportType, trackFilterClick } from './utils/';
import {
    trackFilterClose,
    trackFilterOpen,
    trackFilterUpdate
} from './utils/filters.tracking';
import {
    getDefaultForSaleFilters,
    getForSaleFilters
} from './utils/getForSaleFilters';
import { SelectRadius } from './Radius';
import { SelectDuration, SelectTransportType } from './TravelTime';
import { Stack } from '@zoopla-eng/zds-components/stack';
import { useSearchBarExperiment } from './temporary/useSearchBarExperiment';

const getSearchPath = (
    propertyType: PropertyType | 'retirement' | 'bungalow',
    searchPath: string | string[]
) => {
    const searchPaths = {
        property: 'property',
        houses: 'houses',
        flats: 'flats',
        farms_land: 'property',
        retirement: 'retirement',
        bungalow: 'bungalows'
    };

    if (!searchPath || typeof searchPath === 'string') {
        return [searchPaths[propertyType]];
    }

    // there is a bug in LSRP and perhaps Next itself where pushing to the router (and triggering GIP) causes the url params to appear as a path part in router.query['search-path'] i.e ['property', 'london', '?q=london']
    const validSearchPath = searchPath.filter(
        (v) => !v.startsWith('?') && v !== '_static' && v !== 'v1' && v !== 'v2'
    );

    return [searchPaths[propertyType], ...validSearchPath.slice(1)];
};

export const ForSaleFilters = ({
    container
}: {
    container: HTMLElement | null;
}) => {
    const {
        query,
        push,
        pathname: currentPageRouteFilePath,
        isReady
    } = useRouter();

    const [formValue, updateFormValue] = useState(
        getForSaleFilters(query, currentPageRouteFilePath)
    );

    useEffect(() => {
        updateFormValue(getForSaleFilters(query, currentPageRouteFilePath));
    }, [query, currentPageRouteFilePath]);

    const [isOpen, setIsOpen] = useState(false);

    /*
    Todo: cleanup optimizely experiment once complete
    ---
    SR-1932 Display Recent locations and categorise autosuggestions within search initiation
    */
    const isUsingSearchBar = useSearchBarExperiment();

    const isTravelTimeSearch = Boolean(
        getDuration(formValue) && getTransportType(formValue)
    );

    const onSubmit = async (e: { preventDefault: () => void }) => {
        e.preventDefault();

        const updatedQuery = {
            'search-path': '', // keep typescript happy
            ...query,
            ...formValue,
            pn: ''
        };

        const searchType =
            formValue['new_homes'] === 'only' ? 'new-homes' : 'for-sale';

        let updatedSearchPath = getSearchPath(
            formValue['property_type'] as PropertyType,
            query['search-path'] ?? ''
        );

        if (
            formValue['is_retirement_home'] === 'true' &&
            formValue['property_type'] === 'property'
        ) {
            delete (updatedQuery as Partial<typeof updatedQuery>)[
                'is_retirement_home'
            ];

            updatedSearchPath = getSearchPath('retirement', updatedSearchPath);
        }

        if (formValue['new_homes'] !== 'exclude') {
            delete (updatedQuery as Partial<typeof updatedQuery>)['new_homes'];
        }

        if (
            formValue['property_sub_type'].length === 1 &&
            formValue['property_sub_type'][0] === 'bungalow'
        ) {
            updatedSearchPath = getSearchPath('bungalow', updatedSearchPath);
        }

        if (formValue['property_type'] !== 'farms_land') {
            delete (updatedQuery as Partial<typeof updatedQuery>)[
                'property_type'
            ];
        }

        Object.keys(updatedQuery).forEach((key) => {
            if (updatedQuery[key as keyof typeof updatedQuery] === '') {
                delete updatedQuery[key as keyof typeof updatedQuery];
            }
        });

        // 'search-path' is the name of the dynamic route parameter and so we should not apply as a genuine query param below
        const { 'search-path': invalid, ...validParams } = updatedQuery;

        const pathname = currentPageRouteFilePath.includes('/map/')
            ? [searchType, 'map', ...updatedSearchPath]
            : [searchType, ...updatedSearchPath];

        const updatedPathname = `/${pathname.join('/')}/`;

        setIsOpen(false);

        await push({
            pathname: updatedPathname,
            query: {
                ...validParams
            }
        });
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            onSubmit({ preventDefault: () => event.preventDefault() });
        }
    };

    return (
        <>
            <ButtonLeft
                category="secondary"
                Icon={FiltersMedium}
                onClick={() => {
                    setIsOpen(true);
                    trackFilterOpen();
                }}
                disabled={!isReady}
            >
                Filters
            </ButtonLeft>
            <ControlledDialog
                isOpen={isOpen}
                onClose={() => {
                    trackFilterClose();
                    setIsOpen(false);
                }}
                label="Filter your results"
                title="Filter your results"
                footer={
                    <Box
                        display="flex"
                        columnGap="M200"
                        justifyContent="center"
                        width="100%"
                    >
                        <Button
                            category="tertiary"
                            onClick={() => {
                                trackFilterClick();
                                updateFormValue(getDefaultForSaleFilters());
                            }}
                        >
                            Reset filters
                        </Button>
                        <Button
                            category="primary"
                            onClick={(e) => {
                                trackFilterUpdate();
                                onSubmit(e);
                            }}
                        >
                            Update results
                        </Button>
                    </Box>
                }
                container={container}
                mobileVariant="bottomSheet"
                variant="sideSheetRight"
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    rowGap="S400"
                    pr="S400"
                    bpM100={{ px: 'L400' }}
                >
                    {isUsingSearchBar ? (
                        isTravelTimeSearch ? (
                            <>
                                <p
                                    className={`${labelStyle} ${fontGroups.ladle}`}
                                >
                                    Travel time
                                </p>
                                <Stack
                                    flexDirection="row"
                                    flexWrap="wrap"
                                    columnGap="S400"
                                    rowGap="S400"
                                >
                                    <SelectDuration
                                        value={formValue.duration}
                                        onChange={(duration) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                duration
                                            }));
                                        }}
                                    />
                                    <SelectTransportType
                                        value={formValue.transport_type}
                                        onChange={(transportType) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                transport_type: transportType
                                            }));
                                        }}
                                    />
                                </Stack>
                                <Divider direction="horizontal" />
                            </>
                        ) : (
                            <>
                                <SelectRadius
                                    value={formValue.radius}
                                    onChange={(radius) => {
                                        trackFilterClick();
                                        updateFormValue((oldFormValue) => ({
                                            ...oldFormValue,
                                            radius
                                        }));
                                    }}
                                />
                                <Divider direction="horizontal" />
                            </>
                        )
                    ) : null}
                    <p className={`${labelStyle} ${fontGroups.ladle}`}>
                        Bedrooms
                    </p>
                    <Bedrooms
                        value={{
                            bedsMin: formValue.beds_min as BedsMin,
                            bedsMax: formValue.beds_max as BedsMax
                        }}
                        onChange={({ bedsMin, bedsMax }) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                beds_min: bedsMin,
                                beds_max: bedsMax
                            }));
                        }}
                    />
                    <Divider direction="horizontal" />
                    <p className={`${labelStyle} ${fontGroups.ladle}`}>Price</p>
                    <Price
                        value={{
                            priceMin: formValue.price_min as PriceMin,
                            priceMax: formValue.price_max as PriceMax,
                            priceFrequency:
                                formValue.price_frequency as PriceFrequency
                        }}
                        onChange={({ priceMin, priceMax, priceFrequency }) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                price_min: priceMin,
                                price_max: priceMax,
                                price_frequency: priceFrequency
                            }));
                        }}
                    />
                    <Divider direction="horizontal" />
                    <p className={`${labelStyle} ${fontGroups.ladle}`}>
                        Property type
                    </p>
                    <PropertyType
                        value={{
                            propertyType:
                                formValue.property_type as PropertyType,
                            propertySubType:
                                formValue.property_sub_type as PropertySubType
                        }}
                        onChange={({ propertySubType, propertyType }) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                property_sub_type: propertySubType,
                                property_type: propertyType
                            }));
                        }}
                    />
                    <Divider direction="horizontal" />
                    <p className={`${labelStyle} ${fontGroups.ladle}`}>
                        Include, exclude & show only
                    </p>
                    <HomeType
                        value={{
                            newHomes: formValue.new_homes as NewHomes,
                            isRetirementHome:
                                formValue.is_retirement_home as IsRetirementHome,
                            isSharedOwnership:
                                formValue.is_shared_ownership as IsSharedOwnership,
                            isAuction: formValue.is_auction as IsAuction
                        }}
                        onChange={({
                            newHomes,
                            isRetirementHome,
                            isSharedOwnership,
                            isAuction
                        }) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                new_homes: newHomes,
                                is_retirement_home: isRetirementHome,
                                is_shared_ownership: isSharedOwnership,
                                is_auction: isAuction
                            }));
                        }}
                    />
                    <Divider direction="horizontal" />
                    <p className={`${labelStyle} ${fontGroups.ladle}`}>
                        Must-haves
                    </p>
                    <Features
                        value={formValue.feature as Features}
                        onChange={(feature) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                feature: feature
                            }));
                        }}
                    />
                    <Divider direction="horizontal" />
                    <p className={`${labelStyle} ${fontGroups.ladle}`}>
                        Property status
                    </p>
                    <PropertyStatus
                        value={{
                            buyerIncentive:
                                formValue.buyer_incentive as BuyerIncentive,
                            chainFree: formValue.chain_free as ChainFree,
                            reducedPrice:
                                formValue.reduced_price_only as ReducedPrice,
                            includeSold: formValue.include_sold as IncludeSold
                        }}
                        onChange={({
                            buyerIncentive,
                            chainFree,
                            reducedPrice,
                            includeSold
                        }) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                buyer_incentive: buyerIncentive,
                                chain_free: chainFree,
                                reduced_price_only: reducedPrice,
                                include_sold: includeSold
                            }));
                        }}
                    />
                    <Divider direction="horizontal" />
                    <AddedToSite
                        value={formValue.added as Added}
                        onChange={(added: Added) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                added: added
                            }));
                        }}
                    />
                    <Divider direction="horizontal" />
                    <Keywords
                        value={formValue.keywords as Keywords}
                        onChange={(keywords) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                keywords: keywords
                            }));
                        }}
                        onKeyDown={handleKeyDown}
                    />
                </Box>
            </ControlledDialog>
        </>
    );
};
